import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import {
  AddToCartMutation,
  AddToCartMutationVariables,
  AdjustItemQuantityMutation,
  AdjustItemQuantityMutationVariables,
  Mutation,
  MutationAddProductInquiryArgs,
  OrderLine,
  Product,
  ProductVariant,
  SearchResult,
  SubmitProductInquiryInput,
  SubmitProductInquiryResult,
  TransitionToAddingItemsMutation,
} from '../../../../graphql/generated/generated-types';
import { FormatSearchResultPriceTemplatePipe } from '../../../pipes/format-search-result-price-template.pipe';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DataService } from '../../../../core/providers/data/data.service';
import { StateService } from '../../../../core/state/old/state.service';
import { MessageService } from 'primeng/api';
import { FormatPricePipe } from '../../../pipes/format-price.pipe';
import {
  ADD_TO_CART,
  ADJUST_ITEM_QUANTITY,
} from '../price-and-actions-block/product-cart.graphql';
import { debounceTime, map, of, Subject, take, takeUntil } from 'rxjs';
import {
  IRequestItemDialogData,
  RequestPartDialogComponent,
} from '../../../dialogs/request-part-dialog/request-part-dialog.component';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import {
  IToastActions,
  ToastSeverity,
} from '../../../notification/notification.component';
import { TRANSITION_TO_ADDING_ITEMS } from '../../../../routes/checkout/checkout-process.graphql';

export enum CartButtonType {
  Big = 'Big',
  Medium = 'Medium',
}
@Component({
  selector: 'app-cart-button',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    RippleModule,
    FormatSearchResultPriceTemplatePipe,
    FormatPricePipe,
    ProgressSpinnerModule,
    RequestPartDialogComponent,
  ],
  templateUrl: './cart-button.component.html',
  styleUrl: './cart-button.component.scss',
})
export class CartButtonComponent implements OnInit {
  @Input() productVariant!: ProductVariant;
  @Input() searchResult!: SearchResult;
  @Input() product!: Product;

  @Input() quantity!: number | undefined;
  @Input() showPrice: boolean = false;
  @Input() buttonHeight: number = 40;
  @Input() buttonType: CartButtonType = CartButtonType.Big;
  @Input() selectedAsset: { id: string; preview: string };
  @Input() qtyInCart: { [id: string]: number } = {};
  @Input() orderLineInCart: { [id: string]: OrderLine } = {};
  @Output() quantityChange = new EventEmitter<number>();
  @Output() subscribeToNewActiveOrder: EventEmitter<any> =
    new EventEmitter<any>();
  public itemInCart: boolean = false;

  public loading: boolean = false;

  public stockLevel!: number;
  public itemRequested: boolean = false;

  private quantityUpdateSubject = new Subject<{
    variantId: any;
    change: number;
  }>();
  private quantityMap = new Map<any, { id: string; qty: number }>();

  constructor(
    private dataService: DataService,
    private stateService: StateService,
    private messageService: MessageService,
    private dialogService: DialogService
  ) {
    this.setupQuantityUpdateListener();
  }

  ngOnInit(): void {
    this.setStockLevel();
  }

  private setStockLevel() {
    if (this.product) {
      this.stockLevel = this.convertStringToNumber(
        this.product.variants[0].stockLevel || '0'
      );
    }

    if (this.productVariant) {
      this.stockLevel = this.convertStringToNumber(
        this.productVariant.stockLevel
      );
    }

    if (this.searchResult) {
      this.stockLevel = this.convertStringToNumber(
        this.searchResult.customAttributes?.stockLevel || '0'
      );
    }
  }

  public convertStringToNumber(value: string): number {
    return parseFloat(value);
  }

  public openRequestPartDialog() {
    const dialogRef: DynamicDialogRef = this.dialogService.open(
      RequestPartDialogComponent,
      {
        modal: true,
        dismissableMask: true,
        styleClass: 'c-dialog medium-dialog',
        header: 'Request item',
        data: {
          productVariantId:
            this.searchResult?.productVariantId ||
            this.productVariant?.id ||
            this.product?.variants[0]?.id,
          name:
            this.searchResult?.productName ||
            this.productVariant?.name ||
            this.product?.name,
          slug:
            this.searchResult?.slug ||
            this.productVariant?.product?.slug ||
            this.product?.slug,
          manufacturer:
            this.searchResult?.customAttributes?.manufacturer ||
            this.productVariant?.product?.customAttributes?.manufacturer ||
            this.product?.customAttributes?.manufacturer,
          sku:
            this.searchResult?.sku ||
            this.productVariant?.sku ||
            this.product?.variants[0]?.sku,
          featuredAsset:
            this.searchResult?.productAsset ||
            this.productVariant?.featuredAsset ||
            this.selectedAsset ||
            this.product?.variants[0]?.featuredAsset,
        } as IRequestItemDialogData,
      }
    );
  }

  public incrementVariantInCart(variantId: any, currentQuantity: number) {
    this.queueQuantityChange(variantId, 1, currentQuantity);
  }

  public decrementVariantFromCart(variantId: any, currentQuantity: number) {
    this.queueQuantityChange(variantId, -1, currentQuantity);
  }

  private queueQuantityChange(
    variantId: any,
    change: number,
    currentQuantity: number
  ) {
    const id = this.orderLineInCart[variantId].id;

    // Instantly update the quantity in the cart for UI
    if (!this.qtyInCart[variantId]) {
      this.qtyInCart[variantId] = currentQuantity;
    }
    this.qtyInCart[variantId] += change;

    // Update the quantity map
    const existingEntry = this.quantityMap.get(variantId);
    if (existingEntry) {
      existingEntry.qty += change;
    } else {
      this.quantityMap.set(variantId, { id, qty: currentQuantity + change });
    }

    // Notify the subject to trigger debounce
    this.quantityUpdateSubject.next({ variantId, change });
  }

  private setupQuantityUpdateListener() {
    this.quantityUpdateSubject
      .pipe(debounceTime(300)) // Wait for 400ms after the last click
      .subscribe(() => {
        this.quantityMap.forEach((value, variantId) => {
          // Make a single API call for each updated variant
          this.triggerAdjustItemQuantity(value.id, value.qty);
        });

        // Clear the quantity map after processing
        this.quantityMap.clear();
      });
  }

  private triggerAdjustItemQuantity(id: string, qty: number) {
    this.dataService
      .mutate<AdjustItemQuantityMutation, AdjustItemQuantityMutationVariables>(
        ADJUST_ITEM_QUANTITY,
        { id, qty }
      )
      .pipe(take(1))
      .subscribe(({ adjustOrderLine }) => {
        switch (adjustOrderLine.__typename) {
          case 'Order':
            break;
          case 'InsufficientStockError':
          case 'NegativeQuantityError':
          case 'OrderLimitError':
          case 'OrderModificationError':
            this.messageService.add({
              severity: ToastSeverity.Error,
              key: 'notifications',
              summary: 'Error',
              detail: `${adjustOrderLine.message}`,
            });
            break;
        }
  
      });
  }

  public addVariantToCart(
    variantId: any,
    qty: number,
    showNotification?: boolean
  ) {
    this.loading = true;
    this.dataService
      .mutate<AddToCartMutation, AddToCartMutationVariables>(ADD_TO_CART, {
        variantId: variantId,
        qty,
      })
      .pipe(map((data) => data as Mutation))
      .subscribe({
        next: (response: Mutation | any) => {
          switch (response.addItemToOrder.__typename) {
            case 'Order':
              this.stateService.setState(
                'activeOrderId',
                response.addItemToOrder ? response.addItemToOrder.id : null
              );

              this.messageService.add({
                severity: ToastSeverity.Custom,
                life: 2000,
                key: 'notifications',
                summary: 'Added to cart',
                data: {
                  searchResult: this.searchResult,
                  productVariant: this.productVariant,
                  product: this.product,
                  quantity: this.quantity,
                  selectedAsset: this.selectedAsset,
                  actions: { viewCart: true } as IToastActions,
                },
              });
              this.quantityChange.emit(1);
              break;
            case 'OrderModificationError':
              this.dataService
                .mutate<TransitionToAddingItemsMutation>(
                  TRANSITION_TO_ADDING_ITEMS
                )
                .pipe(take(1))
                .subscribe((response) => {
                  switch (response?.transitionOrderToState?.__typename) {
                    case 'Order':
                      this.addVariantToCart(variantId, qty);
                      break;
                    case 'OrderStateTransitionError':
                      this.messageService.add({
                        severity: ToastSeverity.Warning,
                        life: 2000,
                        key: 'notifications',
                        summary: 'Error',
                        detail: `${response.transitionOrderToState?.message}`,
                        data: {
                          searchResult: this.searchResult,
                          productVariant: this.productVariant,
                          product: this.product,
                          quantity: this.quantity,
                          selectedAsset: this.selectedAsset,
                          actions: {
                            requestMore: true,
                            viewCart: true,
                          } as IToastActions,
                        },
                      });
                      break;
                  }
                });
              break;
            case 'OrderLimitError':
            case 'NegativeQuantityError':
            case 'InsufficientStockError':
              this.messageService.add({
                severity: ToastSeverity.Warning,
                life: 2000,
                key: 'notifications',
                summary: 'Error',
                detail: `${response.addItemToOrder.message}`,
                data: {
                  searchResult: this.searchResult,
                  productVariant: this.productVariant,
                  product: this.product,
                  quantity: this.quantity,
                  selectedAsset: this.selectedAsset,
                  actions: {
                    requestMore: true,
                    viewCart: true,
                  } as IToastActions,
                },
              });
              break;
          }
          this.loading = false;
        },
      });
  }
}
